import { useState, useEffect, useMemo, useCallback } from "react";
import {
  getBatchProcesses, deleteBatchProcess,
  emptyTrash, restoreBatchProcess,
} from "../api/adminApi";
import { friendlyMessageForApiError } from "../api/errorUtils";
import { getStatus } from "../pages/batchProcessHelpers";

const PAGE_SIZE = 100;

// Map the UI status-filter chip to the server enum name. "ALL" (and trash view)
// send no status so the server returns every status in scope.
const statusParam = (statusFilter) =>
  statusFilter && statusFilter !== "ALL" ? statusFilter : undefined;

const EMPTY_COUNTS = { total: 0, pending: 0, inProgress: 0, completed: 0, failed: 0, deleted: 0 };

export function useBatchProcesses(token) {
  // ─── Server-driven page state ────────────────────────────────────────────────
  const [items, setItems]                           = useState([]);
  const [total, setTotal]                           = useState(0);
  const [counts, setCounts]                         = useState(EMPTY_COUNTS);
  const [page, setPage]                             = useState(0);
  const [loading, setLoading]                       = useState(false);
  const [error, setError]                           = useState("");
  const [rawErrorDetails, setRawErrorDetails]       = useState(null);
  const [tick, setTick]                             = useState(0);

  // ─── Filters / sort (all applied server-side) ────────────────────────────────
  const [statusFilter, setStatusFilter]             = useState("ALL");
  const [searchInput, setSearchInput]               = useState(""); // live input value
  const [search, setSearch]                         = useState(""); // debounced/committed
  const [dateFrom, setDateFrom]                     = useState("");
  const [dateTo, setDateTo]                         = useState("");
  const [showDeleted, setShowDeleted]               = useState(false);
  const [sort, setSort]                             = useState("created");
  const [dir, setDir]                               = useState("desc");

  // ─── Selection (scoped to the current page) ──────────────────────────────────
  const [selectMode, setSelectMode]                 = useState(false);
  const [selectedIds, setSelectedIds]               = useState(new Set());
  // Per-row confirmation / in-progress
  const [confirmId, setConfirmId]                   = useState(null);
  const [deletingId, setDeletingId]                 = useState(null);
  const [restoringId, setRestoringId]               = useState(null);
  const [confirmPermanentId, setConfirmPermanentId] = useState(null);
  const [permanentDeletingId, setPermanentDeletingId] = useState(null);
  // Bulk operation flags
  const [bulkDeleting, setBulkDeleting]             = useState(false);
  const [bulkRestoring, setBulkRestoring]           = useState(false);
  const [permanentBulkDeleting, setPermanentBulkDeleting] = useState(false);
  // Trash
  const [emptyingTrash, setEmptyingTrash]           = useState(false);
  const [confirmEmptyTrash, setConfirmEmptyTrash]   = useState(false);
  const [confirmBulkPermanent, setConfirmBulkPermanent] = useState(false);
  // Expanded row
  const [expandedId, setExpandedId]                 = useState(null);

  // ─── Helpers ────────────────────────────────────────────────────────────────
  const setApiError = (e) => {
    setError(friendlyMessageForApiError(e));
    setRawErrorDetails(e?.details ?? e);
  };

  const removeFromPage = (ids) =>
    setItems(prev => prev.filter(b => !ids.includes(b.id ?? b.Id)));
  const deselectIds    = (ids) =>
    setSelectedIds(prev => { const s = new Set(prev); ids.forEach(id => s.delete(id)); return s; });
  const reload         = () => setTick(t => t + 1);

  // Reset to the first page whenever a filter/sort changes so the user never lands
  // on an out-of-range page. Selection is page-scoped, so clear it too.
  const goFirstPage = () => { setPage(0); setSelectedIds(new Set()); };

  // ─── Debounce the search box (server hit at most every 400ms of quiet) ────────
  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(prev => (prev === searchInput ? prev : searchInput));
      setPage(0);
    }, 400);
    return () => clearTimeout(id);
  }, [searchInput]);

  // ─── Data loading ─────────────────────────────────────────────────────────────
  // silent=true skips the loading flag + error reset so the 30s background refresh
  // never flickers the UI.
  const load = useCallback(async ({ silent = false } = {}) => {
    if (!token) return;
    if (!silent) { setLoading(true); setError(""); setRawErrorDetails(null); }
    try {
      const data = await getBatchProcesses(token, {
        skip: page * PAGE_SIZE,
        take: PAGE_SIZE,
        status: showDeleted ? undefined : statusParam(statusFilter),
        search: search || undefined,
        from: dateFrom || undefined,
        to: dateTo || undefined,
        showDeleted: showDeleted || undefined,
        sort,
        dir,
      });
      const serverTotal = Number(data?.total) || 0;
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotal(serverTotal);
      if (data?.counts) setCounts({ ...EMPTY_COUNTS, ...data.counts });
      // Clamp: if a deletion emptied the current (deep) page, drop back to the last
      // page that still has rows instead of stranding the user on an empty page.
      const maxPage = Math.max(0, Math.ceil(serverTotal / PAGE_SIZE) - 1);
      if (page > maxPage) { setPage(maxPage); return; }
      // Drop any selected ids that are no longer on the visible page.
      const pageIds = new Set((data?.items ?? []).map(b => b.id ?? b.Id));
      setSelectedIds(prev => {
        const cleaned = new Set([...prev].filter(id => pageIds.has(id)));
        return cleaned.size === prev.size ? prev : cleaned;
      });
    } catch (e) {
      if (!silent) setApiError(e);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [token, page, statusFilter, search, dateFrom, dateTo, showDeleted, sort, dir]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  // Silent background refresh every 30 s — never resets UI state
  useEffect(() => {
    if (!token) return;
    const id = setInterval(() => { load({ silent: true }); }, 30000);
    return () => clearInterval(id);
  }, [token, load]);

  // ─── Filter/sort setters (reset paging + selection) ──────────────────────────
  const changeStatusFilter = useCallback((v) => { setStatusFilter(v); goFirstPage(); }, []);
  const changeDateFrom     = useCallback((v) => { setDateFrom(v);     goFirstPage(); }, []);
  const changeDateTo       = useCallback((v) => { setDateTo(v);       goFirstPage(); }, []);

  // Toggle a column's sort; first click on a new column sorts descending, clicking
  // the active column flips direction.
  const toggleSort = useCallback((column) => {
    if (sort === column) {
      setDir(d => (d === "asc" ? "desc" : "asc"));
    } else {
      setSort(column);
      setDir("desc");
    }
    setPage(0);
    setSelectedIds(new Set());
  }, [sort]);

  // ─── Mutation handlers ───────────────────────────────────────────────────────
  // mode: "soft" (default) hides docs from search; "hard" also deletes archive + index files on disk.
  const handleDelete = useCallback(async (id, mode = "soft") => {
    setDeletingId(id);
    setConfirmId(null);
    try {
      await deleteBatchProcess(token, id, mode);
      // Soft-delete removes the row from the active list; hard-delete removes it from
      // the trash view. Either way it leaves the current page — drop it for instant
      // feedback, then reload to refresh totals/counts.
      removeFromPage([id]);
      reload();
    } catch (e) {
      setApiError(e);
    } finally {
      setDeletingId(null);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBulkDelete = useCallback(async (mode = "soft") => {
    if (!selectedIds.size) return;
    setBulkDeleting(true);
    setError("");
    const ids = [...selectedIds];
    const results = await Promise.allSettled(ids.map(id => deleteBatchProcess(token, id, mode)));
    const succeeded = ids.filter((_, i) => results[i].status === "fulfilled");
    const failed    = results.filter(r => r.status === "rejected");
    if (succeeded.length) { removeFromPage(succeeded); deselectIds(succeeded); }
    if (failed.length) setApiError({ message: `${failed.length} batch(es) could not be deleted.`, details: failed[0].reason?.details ?? failed[0].reason });
    setBulkDeleting(false);
    reload();
  }, [token, selectedIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // Restore returns the server-resolved status (Completed/Failed/Pending) — refetch
  // so the UI reflects the actual state instead of optimistically forcing Pending.
  const handleRestore = useCallback(async (id) => {
    setRestoringId(id);
    try {
      await restoreBatchProcess(token, id);
      removeFromPage([id]); // leaves the trash view
      reload();
    } catch (e) {
      setApiError(e);
    } finally {
      setRestoringId(null);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBulkRestore = useCallback(async () => {
    if (!selectedIds.size) return;
    setBulkRestoring(true);
    setError("");
    const ids = [...selectedIds];
    const results = await Promise.allSettled(ids.map(id => restoreBatchProcess(token, id)));
    const succeeded = ids.filter((_, i) => results[i].status === "fulfilled");
    const failed    = results.filter(r => r.status === "rejected");
    if (succeeded.length) { removeFromPage(succeeded); deselectIds(succeeded); }
    if (failed.length) setApiError({ message: `${failed.length} batch(es) could not be restored.`, details: failed[0].reason?.details ?? failed[0].reason });
    setBulkRestoring(false);
    reload();
  }, [token, selectedIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // Hard-delete is queued server-side: REST writes FileDeletionRequestedAt, the
  // worker drains the queue (poll 10s) and stamps FilesDeletedAt. Drop the row
  // optimistically; the reload (and the 30s refresh) reconcile.
  const scheduleDelayedRefresh = () => setTimeout(reload, 12000);

  const handlePermanentDelete = useCallback(async (id, confirmation) => {
    setPermanentDeletingId(id);
    setConfirmPermanentId(null);
    try {
      await deleteBatchProcess(token, id, "hard", confirmation);
      removeFromPage([id]);
      reload();
      scheduleDelayedRefresh();
    } catch (e) {
      setApiError(e);
    } finally {
      setPermanentDeletingId(null);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePermanentBulkDelete = useCallback(async (confirmation) => {
    if (!selectedIds.size) return;
    setPermanentBulkDeleting(true);
    setError("");
    const ids = [...selectedIds];
    const results = await Promise.allSettled(ids.map(id => deleteBatchProcess(token, id, "hard", confirmation)));
    const succeeded = ids.filter((_, i) => results[i].status === "fulfilled");
    const failed    = results.filter(r => r.status === "rejected");
    if (succeeded.length) { removeFromPage(succeeded); deselectIds(succeeded); scheduleDelayedRefresh(); }
    if (failed.length) setApiError({ message: `${failed.length} batch(es) could not be hard-deleted.`, details: failed[0].reason?.details ?? failed[0].reason });
    setPermanentBulkDeleting(false);
    reload();
  }, [token, selectedIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // EmptyTrash queues file cleanup for EVERY trashed batch server-side (not just the
  // visible page). Requires the typed "DELETE" confirmation token from the modal.
  const handleEmptyTrash = useCallback(async (confirmation) => {
    setEmptyingTrash(true);
    setConfirmEmptyTrash(false);
    setError("");
    try {
      await emptyTrash(token, confirmation);
      setSelectedIds(new Set());
      reload();
      scheduleDelayedRefresh();
    } catch (e) {
      setApiError(e);
    } finally {
      setEmptyingTrash(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── UI actions ──────────────────────────────────────────────────────────────
  const toggleExpanded = (id) => setExpandedId(prev => prev === id ? null : id);

  const toggleShowDeleted = () => {
    setShowDeleted(v => !v);
    setSelectedIds(new Set());
    setSelectMode(false);
    setConfirmEmptyTrash(false);
    setPage(0);
  };

  const exitSelectMode = () => { setSelectMode(false); setSelectedIds(new Set()); };

  const toggleOne = (id) => setSelectedIds(prev => {
    const s = new Set(prev);
    s.has(id) ? s.delete(id) : s.add(id);
    return s;
  });

  // ─── Derived data ────────────────────────────────────────────────────────────
  // Stats come straight from the server (correct at any table size). Trash view
  // hides the KPI cards, so this is only consumed in the active view + trash badge.
  const stats = useMemo(() => ({
    total:      counts.total,
    pending:    counts.pending,
    inProgress: counts.inProgress,
    completed:  counts.completed,
    failed:     counts.failed,
    deleted:    counts.deleted,
  }), [counts]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const selectableIds = useMemo(() =>
    showDeleted
      ? items.map(b => b.id ?? b.Id)
      : items.filter(b => ["Pending", "Failed", "Completed"].includes(getStatus(b))).map(b => b.id ?? b.Id),
    [items, showDeleted]
  );

  const allSelected  = selectableIds.length > 0 && selectableIds.every(id => selectedIds.has(id));
  const someSelected = selectableIds.some(id => selectedIds.has(id));
  const toggleAll    = () => setSelectedIds(allSelected ? new Set() : new Set(selectableIds));

  // ─── Public API ──────────────────────────────────────────────────────────────
  return {
    // data
    items, stats, hasItems: items.length > 0,
    loading, error, rawErrorDetails,
    // paging
    page, setPage, total, totalPages, pageSize: PAGE_SIZE,
    // sort
    sort, dir, toggleSort,
    // filters
    statusFilter, setStatusFilter: changeStatusFilter,
    search: searchInput, setSearch: setSearchInput,
    dateFrom, setDateFrom: changeDateFrom,
    dateTo, setDateTo: changeDateTo,
    // selection
    selectedIds, selectMode, setSelectMode,
    selectableIds, allSelected, someSelected,
    exitSelectMode, toggleOne, toggleAll,
    // trash
    showDeleted, toggleShowDeleted,
    confirmEmptyTrash, setConfirmEmptyTrash,
    confirmBulkPermanent, setConfirmBulkPermanent,
    emptyingTrash,
    // per-row state
    confirmId, setConfirmId, deletingId,
    restoringId,
    confirmPermanentId, setConfirmPermanentId, permanentDeletingId,
    // bulk state
    bulkDeleting, bulkRestoring, permanentBulkDeleting,
    // expanded row
    expandedId, toggleExpanded,
    // manual refresh trigger
    setTick,
    // handlers
    handleDelete, handleBulkDelete,
    handleRestore, handleBulkRestore,
    handlePermanentDelete, handlePermanentBulkDelete,
    handleEmptyTrash,
  };
}
